import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarBlank } from "@phosphor-icons/react";

export const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

// true di layar sentuh (HP/tablet). Dipakai untuk mengganti floating-UI
// Radix (Popover/Select) dengan kontrol native yang selalu merespons sentuhan.
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    setCoarse(mq.matches);
    const fn = (e) => setCoarse(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return coarse;
}

// Rupiah input: shows thousand separators live (8.000.000), emits a Number.
export function CurrencyInput({ value, onChange, className, placeholder = "0", ...props }) {
  const fmt = (n) => (!n || isNaN(Number(n)) || Number(n) === 0) ? "" : Number(n).toLocaleString("id-ID");
  const [text, setText] = useState(() => fmt(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(fmt(value));
  }, [value]);

  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const num = digits === "" ? 0 : parseInt(digits, 10);
    setText(digits === "" ? "" : num.toLocaleString("id-ID"));
    onChange(num);
  };

  return (
    <Input
      placeholder={placeholder}
      {...props}
      inputMode="numeric"
      value={text}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setText(fmt(value)); }}
      onChange={handle}
      className={className}
    />
  );
}

// Percentage input: free typing, no forced leading zero, emits a Number.
export function PercentInput({ value, onChange, className, placeholder = "0", ...props }) {
  const fmt = (n) => (n === "" || n === null || n === undefined || Number(n) === 0) ? "" : String(n);
  const [text, setText] = useState(() => fmt(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(fmt(value));
  }, [value]);

  const handle = (e) => {
    let raw = e.target.value.replace(/[^\d.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) raw = parts[0] + "." + parts.slice(1).join("");
    setText(raw);
    onChange(raw === "" ? 0 : parseFloat(raw) || 0);
  };

  return (
    <Input
      placeholder={placeholder}
      {...props}
      inputMode="decimal"
      value={text}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setText(fmt(value)); }}
      onChange={handle}
      className={className}
    />
  );
}

// Click-to-pick calendar; value & onChange use "DD/MM/YYYY" strings.
export function DatePicker({ value, onChange, placeholder = "Pilih tanggal", testId }) {
  const parse = (s) => {
    if (!s) return undefined;
    const [d, m, y] = String(s).split("/").map(Number);
    if (!d || !m || !y) return undefined;
    return new Date(y, m - 1, d);
  };
  const fmt = (date) => `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
  const toISO = (s) => {
    const d = parse(s);
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const fromISO = (iso) => {
    if (!iso) return;
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return;
    onChange(fmt(new Date(y, m - 1, d)));
  };
  const [open, setOpen] = useState(false);
  const coarse = useCoarsePointer();
  const selected = parse(value);

  // Layar sentuh: input date native (kalender bawaan OS, selalu terbuka).
  if (coarse) {
    return (
      <Input
        type="date"
        value={toISO(value)}
        min="2023-01-01"
        max="2030-12-31"
        onChange={(e) => fromISO(e.target.value)}
        data-testid={testId}
        className="h-10 min-w-0 w-full px-2 text-sm tabular-nums dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:shrink-0 [&::-webkit-datetime-edit]:min-w-0"
        aria-label={placeholder}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full min-w-0 h-10 justify-start gap-2.5 px-3.5 font-normal" data-testid={testId}>
          <CalendarBlank size={18} className="text-muted-foreground shrink-0" />
          {selected ? <span className="truncate min-w-0">{fmt(selected)}</span> : <span className="text-muted-foreground truncate min-w-0">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { if (d) { onChange(fmt(d)); setOpen(false); } }}
          captionLayout="dropdown-buttons"
          fromYear={2023}
          toYear={2030}
          classNames={{
            caption_label: "hidden",
            caption_dropdowns: "flex gap-2 justify-center items-center",
            vhidden: "sr-only",
            dropdown: "rounded-md border border-input bg-background px-2 py-1 text-sm font-medium",
            nav: "hidden",
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// Two separate selects: month + year (no long combined list).
export function MonthYearPicker({ month, year, onChange, years, monthTestId, yearTestId, className }) {
  const coarse = useCoarsePointer();

  // Layar sentuh: <select> native (sheet pilihan bawaan OS, selalu terbuka).
  if (coarse) {
    const nativeCls = "flex h-10 w-full min-w-0 items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:[color-scheme:dark]";
    return (
      <div className={`grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2 ${className || ""}`}>
        <select
          value={String(month)}
          onChange={(e) => onChange(Number(e.target.value), year)}
          data-testid={monthTestId}
          className={nativeCls}
          aria-label="Bulan"
        >
          {MONTHS_ID.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
        </select>
        <select
          value={String(year)}
          onChange={(e) => onChange(month, Number(e.target.value))}
          data-testid={yearTestId}
          className={nativeCls}
          aria-label="Tahun"
        >
          {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2 ${className || ""}`}>
      <Select value={String(month)} onValueChange={(v) => onChange(Number(v), year)}>
        <SelectTrigger data-testid={monthTestId}><SelectValue placeholder="Bulan" /></SelectTrigger>
        <SelectContent>
          {MONTHS_ID.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => onChange(month, Number(v))}>
        <SelectTrigger data-testid={yearTestId}><SelectValue placeholder="Tahun" /></SelectTrigger>
        <SelectContent>
          {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
